import React, { useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FieldError, svgSprite, Tooltip } from '../../../../../hybrid/wrappers';
import css from './FormRadioSwitcher.module.scss';
import classnames from 'classnames';
import { generateCompStylers } from '../../../../../common/utils/styles';

const FormRadio = ({ attribute, hint, disabled, title, compStyle, value, setFormData, errors, options, hardChange }) => {
    const { stylers, classes } = generateCompStylers(compStyle);

    const [val, setVal] = useState(value);
    const changeHandler = useCallback(
        (v) => {
            if (!disabled) {
                setFormData(attribute, v);
                setVal(v);
            }
        },
        [disabled, setFormData, attribute]
    );

    const optionsArray = useMemo(() => {
        if (options.length || options.length === 0) return options;
        return [...Object.keys(options).filter((o) => `${o}` !== '0'), ...Object.keys(options).filter((o) => `${o}` === '0')];
    }, [options]);

    useEffect(() => {
        if (hardChange) {
            setVal(value);
        }
    }, [hardChange, value]);

    if (!options) return null;
    return (
        <div style={stylers.wrapper} className={classnames(css.formGroup, classes.wrapper)}>
            <div style={stylers.content} className={classnames(css.formRadioSwitcher, classes.content)}>
                {!!title && (
                    <div style={stylers.title} className={classnames(css.formRadioSwitcherTitle, classes.title)}>
                        {title?.body}
                    </div>
                )}
                <div
                    style={stylers.togglerBackground}
                    className={classnames(css.formRadioSwitcherToggler, classes.togglerBackground)}
                >
                    {optionsArray.map((name, idx) => {
                        const is_active = (options.length && (val === idx || +val === idx)) || `${val}` === `${name}`;
                        const has_icon =
                            (typeof options[name] === 'object' || typeof name === 'object') &&
                            (options[name]?.['icon'] || name['icon']);
                        return (
                            <React.Fragment key={`${attribute}option${idx}`}>
                                <div
                                    onClick={() => changeHandler(options.length ? idx : parseInt(name) || 0)}
                                    style={stylers.togglerElement}
                                    className={classnames(
                                        css.formRadioSwitcherTogglerElement,
                                        is_active
                                            ? classnames(css.radioSwitcherTogglerElementActive, classes.togglerElementActive)
                                            : {},
                                        has_icon && is_active && css.radioSwitcherTogglerElementActiveIcon,
                                        classes.togglerElement
                                    )}
                                >
                                    {has_icon && is_active ? (
                                        <div className={classnames(css.radioSwitcherTogglerElementIcon)}>
                                            {svgSprite(options[name]?.['icon'] || name['icon'])}
                                        </div>
                                    ) : null}
                                    <p
                                        style={stylers.togglerElementText}
                                        className={classnames(
                                            (options.length && (val === idx || +val === idx)) || `${val}` === `${name}`
                                                ? [css.formRadioSwitcherTogglerText, css.formRadioSwitcherTogglerTextActive]
                                                : [css.formRadioSwitcherTogglerText],
                                            classes.togglerElementText
                                        )}
                                    >
                                        {(typeof options[name] === 'object' && React.isValidElement(name)) ||
                                        (typeof name === 'object' && !React.isValidElement(name))
                                            ? options[name]?.['title'] || name['title']
                                            : options[name] || name}
                                    </p>
                                </div>
                            </React.Fragment>
                        );
                    })}
                </div>
                {!!hint && (
                    <Tooltip text={hint} compStyle={{ inner: css.formInfoTooltip }}>
                        {svgSprite('question-circle-fill', {
                            className: css.formInfoIcon
                        })}
                    </Tooltip>
                )}
            </div>
            {errors && errors.length ? errors.map((err, idx) => <FieldError key={`${attribute}${idx}`}>{err}</FieldError>) : null}
        </div>
    );
};
FormRadio.propTypes = {
    attribute: PropTypes.string,
    hint: PropTypes.node,
    disabled: PropTypes.bool,
    title: PropTypes.object,
    compStyle: PropTypes.object,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    setFormData: PropTypes.func,
    errors: PropTypes.array,
    options: PropTypes.array
};
export default FormRadio;
